import { useState, useEffect, useCallback } from "react";
import { isStandalone } from "@/components/InstallGuideModal";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

type InstallOutcome = "accepted" | "dismissed" | "unavailable";

// Bridged onto window so the event isn't lost if beforeinstallprompt fires
// before any component using this hook has mounted — main.tsx registers an
// early capture into the same global for exactly that reason.
const getGlobalPrompt = (): BeforeInstallPromptEvent | null =>
  (window as any).__pwaInstallPrompt || null;
const setGlobalPrompt = (event: BeforeInstallPromptEvent | null) => {
  (window as any).__pwaInstallPrompt = event;
};

/**
 * Wraps the native `beforeinstallprompt` flow (Chrome/Edge on Android and
 * desktop — Safari never fires this event, iOS install stays textual via
 * InstallGuideModal). `canInstall` is only ever true when the browser has
 * actually offered a native prompt AND the app isn't already installed.
 */
export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
    () => (isStandalone() ? null : getGlobalPrompt()),
  );

  useEffect(() => {
    if (isStandalone()) return;

    if (!deferredPrompt) {
      const existing = getGlobalPrompt();
      if (existing) setDeferredPrompt(existing);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setGlobalPrompt(promptEvent);
      setDeferredPrompt(promptEvent);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const install = useCallback(async (): Promise<InstallOutcome> => {
    const prompt = deferredPrompt || getGlobalPrompt();
    if (!prompt) return "unavailable";

    try {
      await prompt.prompt();
      const result = await prompt.userChoice;
      setGlobalPrompt(null);
      setDeferredPrompt(null);
      return result.outcome;
    } catch {
      setGlobalPrompt(null);
      setDeferredPrompt(null);
      return "dismissed";
    }
  }, [deferredPrompt]);

  return {
    canInstall: !isStandalone() && !!deferredPrompt,
    install,
  };
}
